import React, { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	dismissLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.promo.dismissLabel',
		defaultMessage: 'Dismiss',
		description: 'Accessibility label for the dismiss button on the connector promo card',
	},
});

export interface ConnectorPromoCardBaseProps {
	heading: string;
	body: string;
	icon?: ReactNode;
	primaryCtaLabel: string;
	onPrimaryClick: () => void;
	/** When provided, renders the primary CTA as an external LinkButton instead of a Button. */
	primaryCtaLink?: string;
	secondaryCtaLabel?: string;
	onSecondaryClick?: () => void;
	onDismiss?: () => void;
	testId?: string;
}

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		backgroundColor: token('color.background.information'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		marginBlock: token('space.075'),
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
		marginLeft: 'auto',
	},
});

export const ConnectorPromoCardBase = ({
	heading,
	body,
	icon,
	primaryCtaLabel,
	onPrimaryClick,
	primaryCtaLink,
	secondaryCtaLabel,
	onSecondaryClick,
	onDismiss,
	testId,
}: ConnectorPromoCardBaseProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={styles.container} testId={testId}>
			{icon && <Box as="span">{icon}</Box>}
			<Stack space="space.050">
				<Text size="medium" weight="bold">
					{heading}
				</Text>
				<Text size="small" color="color.text.subtle">
					{body}
				</Text>
			</Stack>
			<Flex xcss={styles.actions}>
				<Inline space="space.100" alignBlock="center">
					{secondaryCtaLabel && onSecondaryClick && (
						<Button
							appearance="subtle"
							onClick={onSecondaryClick}
							testId={testId ? `${testId}--secondary-cta` : undefined}
						>
							{secondaryCtaLabel}
						</Button>
					)}
					{primaryCtaLink ? (
						<LinkButton
							appearance="primary"
							spacing="compact"
							href={primaryCtaLink}
							target="_blank"
							iconAfter={LinkExternalIcon}
							onClick={onPrimaryClick}
							testId={testId ? `${testId}--primary-cta` : undefined}
						>
							{primaryCtaLabel}
						</LinkButton>
					) : (
						<Button
							appearance="primary"
							spacing="compact"
							onClick={onPrimaryClick}
							testId={testId ? `${testId}--primary-cta` : undefined}
						>
							{primaryCtaLabel}
						</Button>
					)}
				</Inline>
				{onDismiss && (
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(messages.dismissLabel)}
						onClick={onDismiss}
						testId={testId ? `${testId}--dismiss` : undefined}
					/>
				)}
			</Flex>
		</Flex>
	);
};
